import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertTriangle, RefreshCw, Send, XCircle, CheckCircle2, Edit3, Trash2, GitBranch, FileDown, FileSignature, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import {
  getOffer, getOfferHistory, transitionOffer, newRevision as apiNewRevision, acceptOffer,
  deleteOffer, updateOffer, generateOfferPdf, contractualizeOffer, restoreOffer,
  OFFER_STATUS_LABELS, OFFER_STATUS_COLORS, extractApiError,
} from '../../lib/subcontractOfferApi';
import GeneratedContractSection from './GeneratedContractSection';

/**
 * Azioni ammesse per ciascuno stato (specchio della state machine backend).
 * `target`: stato di destinazione via /transition, oppure `_action` per azioni dedicate.
 */
const ACTIONS_BY_STATUS = {
  bozza: [
    { key: 'edit',    label: 'Modifica',  icon: Edit3,   variant: 'outline', _action: 'edit' },
    { key: 'delete',  label: 'Elimina',   icon: Trash2,  variant: 'destructive', _action: 'delete' },
    { key: 'emesso',  label: 'Emetti',    icon: Send,    variant: 'default', target: 'emesso' },
  ],
  emesso: [
    { key: 'inviato',    label: 'Segna come inviato', icon: Send,       variant: 'default',     target: 'inviato' },
    { key: 'annullato',  label: 'Annulla',            icon: XCircle,    variant: 'destructive', target: 'annullato' },
    { key: 'new_rev',    label: 'Nuova revisione',    icon: GitBranch,  variant: 'outline',     _action: 'new_rev' },
  ],
  inviato: [
    { key: 'accept',     label: 'Registra accettazione', icon: CheckCircle2, variant: 'default',   _action: 'accept' },
    { key: 'rifiutato',  label: 'Registra rifiuto',      icon: XCircle,      variant: 'destructive', target: 'rifiutato' },
    { key: 'scaduto',    label: 'Segna scaduto',         icon: AlertTriangle,variant: 'outline',    target: 'scaduto' },
    { key: 'annullato',  label: 'Annulla',               icon: XCircle,      variant: 'destructive', target: 'annullato' },
    { key: 'new_rev',    label: 'Nuova revisione',       icon: GitBranch,    variant: 'outline',    _action: 'new_rev' },
  ],
  accettato: [
    { key: 'contractualize', label: 'Genera Contratto', icon: FileSignature, variant: 'default', _action: 'contractualize' },
    { key: 'annullato', label: 'Annulla', icon: XCircle, variant: 'destructive', target: 'annullato' },
  ],
  rifiutato: [
    { key: 'new_rev', label: 'Nuova revisione', icon: GitBranch, variant: 'outline', _action: 'new_rev' },
  ],
  scaduto: [
    { key: 'new_rev', label: 'Nuova revisione', icon: GitBranch, variant: 'outline', _action: 'new_rev' },
  ],
  annullato: [
    { key: 'restore', label: 'Ripristina offerta', icon: RefreshCw, variant: 'default', _action: 'restore' },
  ],
  superato: [],
  contrattualizzato: [],
  convertito_in_contratto: [],
};

/**
 * Dettaglio Offerta di Subappalto — dialog dedicato.
 *
 * Props:
 * - open, onOpenChange
 * - offerId: id offerta da caricare (revisione corrente o specifica)
 * - isAdmin: bool
 * - project: dettaglio progetto (necessario per aprire il wizard di edit)
 * - onChanged: (updatedOffer) => void
 * - onRequestEdit: (offer) => void — callback per aprire il wizard in edit mode
 */
export const SubcontractOfferDetail = ({ open, onOpenChange, offerId, isAdmin, onChanged, onRequestEdit }) => {
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Modali specifici
  const [confirmTransition, setConfirmTransition] = useState(null); // { target, reason }
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [acceptDate, setAcceptDate] = useState('');
  const [acceptNotes, setAcceptNotes] = useState('');
  const [newRevOpen, setNewRevOpen] = useState(false);
  const [newRevReason, setNewRevReason] = useState('');
  // F.5 — Contrattualizzazione
  const [contractualizeOpen, setContractualizeOpen] = useState(false);
  const [contractualizeReason, setContractualizeReason] = useState('');
  const [contractualizing, setContractualizing] = useState(false);
  // Ripristino offerta annullata → bozza
  const [restoreOpen, setRestoreOpen] = useState(false);

  const reload = async () => {
    if (!offerId) return;
    setLoading(true);
    setError(null);
    try {
      const [o, h] = await Promise.all([getOffer(offerId), getOfferHistory(offerId).catch(() => null)]);
      setOffer(o);
      setHistory(h);
    } catch (err) {
      const msg = extractApiError(err, 'Errore caricamento offerta');
      setError(msg);
      if (err?.response?.status === 401) toast.error('Sessione scaduta');
      else if (err?.response?.status === 403) toast.error('Non autorizzato');
      else if (err?.response?.status === 404) toast.error('Offerta non trovata');
      else toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && offerId) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offerId]);

  const actions = useMemo(() => {
    if (!offer) return [];
    const base = ACTIONS_BY_STATUS[offer.status] || [];
    // Rev>1 in bozza: il backend rigetta DELETE (usare transition→annullato).
    // Sostituisco l'azione 'delete' con 'annullato' per l'UX.
    if (offer.status === 'bozza' && offer.offer_revision_number > 1) {
      return base
        .filter(a => a._action !== 'delete')
        .concat([{ key: 'annullato', label: 'Annulla revisione', icon: XCircle, variant: 'destructive', target: 'annullato' }]);
    }
    return base;
  }, [offer]);

  // ==================
  // Handlers azioni
  // ==================
  const handle409 = () => {
    toast.error('I dati sono stati modificati da un altro utente. Ricarica l&apos;offerta prima di continuare.');
    reload();
  };

  const doTransition = async (target, reason) => {
    if (!offer || busy) return;
    setBusy(true);
    try {
      const updated = await transitionOffer(offer.id, target, offer.row_revision, reason);
      toast.success(`Stato aggiornato: ${OFFER_STATUS_LABELS[updated.status] || updated.status}`);
      setOffer(updated);
      if (onChanged) onChanged(updated);
      await reload();
    } catch (err) {
      if (err?.response?.status === 409) { handle409(); }
      else toast.error(extractApiError(err, 'Errore transizione'));
    } finally {
      setBusy(false);
      setConfirmTransition(null);
    }
  };

  const doDelete = async () => {
    if (!offer || busy) return;
    setBusy(true);
    try {
      await deleteOffer(offer.id);
      toast.success('Bozza eliminata');
      onOpenChange(false);
      if (onChanged) onChanged(null);
    } catch (err) {
      toast.error(extractApiError(err, 'Errore eliminazione'));
    } finally {
      setBusy(false);
    }
  };

  const doNewRevision = async () => {
    if (!offer || busy) return;
    if (!newRevReason.trim()) { toast.error('Motivazione obbligatoria'); return; }
    setBusy(true);
    try {
      const nr = await apiNewRevision(offer.id, offer.row_revision, newRevReason.trim());
      toast.success(`Nuova revisione ${nr.offer_number} Rev.${nr.offer_revision_number} creata in bozza`);
      setNewRevOpen(false);
      setNewRevReason('');
      // Ricarico direttamente la nuova revisione
      const fresh = await getOffer(nr.id);
      setOffer(fresh);
      const h = await getOfferHistory(nr.id).catch(() => null);
      setHistory(h);
      if (onChanged) onChanged(fresh);
    } catch (err) {
      if (err?.response?.status === 409) { handle409(); }
      else toast.error(extractApiError(err, 'Errore nuova revisione'));
    } finally {
      setBusy(false);
    }
  };

  const doAccept = async () => {
    if (!offer || busy) return;
    setBusy(true);
    try {
      const notes = [acceptDate && `Data accettazione: ${acceptDate}`, acceptNotes].filter(Boolean).join(' | ');
      const updated = await acceptOffer(offer.id, offer.row_revision, notes || null);
      toast.success('Offerta accettata');
      setOffer(updated);
      setAcceptModalOpen(false);
      setAcceptDate('');
      setAcceptNotes('');
      if (onChanged) onChanged(updated);
      await reload();
    } catch (err) {
      if (err?.response?.status === 409) { handle409(); }
      else toast.error(extractApiError(err, 'Errore accettazione'));
    } finally {
      setBusy(false);
    }
  };

  const doGeneratePdf = async () => {
    if (!offer || busy) return;
    setBusy(true);
    try {
      const resp = await generateOfferPdf(offer.id);
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const filename = resp.headers?.['x-filename']
        || `Offerta_Subappalto_${offer.offer_number}_Rev${offer.offer_revision_number}.pdf`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF generato');
    } catch (err) {
      // F.7 bugfix · Gestione errori di dominio specifici del PDF Offerta.
      // La response può essere un Blob (responseType:'blob'): decodifichiamola.
      let code = null;
      let message = null;
      const data = err?.response?.data;
      try {
        if (data instanceof Blob) {
          const text = await data.text();
          const parsed = JSON.parse(text);
          code = parsed?.detail?.code || null;
          message = parsed?.detail?.message || parsed?.detail || null;
        } else if (typeof data === 'object' && data?.detail) {
          code = data.detail?.code || null;
          message = data.detail?.message || data.detail || null;
        }
      } catch (_) { /* fallback sotto */ }

      if (code === 'FEATURE_DISABLED') {
        toast.error('Modulo Offerte di Subappalto non attivo. Chiedi a un amministratore di attivare il feature flag.');
      } else if (code === 'PDF_NOT_AVAILABLE_ON_DRAFT') {
        toast.error('Il PDF è disponibile solo dopo l\u2019emissione dell\u2019Offerta.');
      } else if (err?.response?.status === 404) {
        toast.error('Offerta non trovata.');
      } else {
        toast.error(message || extractApiError(err, 'Errore generazione PDF'));
      }
    } finally {
      setBusy(false);
    }
  };

  // F.5 — Contrattualizzazione: idempotente, protetta da doppio click e da row_revision stale
  const doContractualize = async () => {
    if (!offer || busy || contractualizing) return;
    setContractualizing(true);
    setBusy(true);
    try {
      const res = await contractualizeOffer(
        offer.id,
        offer.row_revision,
        (contractualizeReason || '').trim() || null,
      );
      if (res?.already_contractualized) {
        toast.info(`Offerta già contrattualizzata (Contratto ${res.subcontract_number || res.subcontract_id})`);
      } else {
        toast.success(`Contratto ${res.subcontract_number} generato`);
      }
      setContractualizeOpen(false);
      setContractualizeReason('');
      await reload();
      if (onChanged) {
        const fresh = await getOffer(offer.id);
        onChanged(fresh);
      }
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.detail?.code;
      if (status === 409 && code === 'ROW_REVISION_MISMATCH') {
        toast.error('L\u2019offerta \u00e8 stata aggiornata da un altro utente. Ricarico e riprova.');
        setContractualizeOpen(false);
        await reload();
      } else if (status === 409 && code === 'ITEMS_ALREADY_ASSIGNED') {
        toast.error(extractApiError(err, 'Alcune lavorazioni risultano gi\u00e0 assegnate a un altro contratto.'));
        setContractualizeOpen(false);
        await reload();
      } else if (status === 400 && code === 'OFFER_NOT_CONTRACTUALIZABLE') {
        toast.error('L\u2019offerta non \u00e8 in stato "accettato" e non pu\u00f2 essere contrattualizzata.');
        setContractualizeOpen(false);
        await reload();
      } else if (status === 404) {
        toast.error('Endpoint non disponibile (feature flag OFF o offerta non trovata).');
      } else {
        toast.error(extractApiError(err, 'Errore contrattualizzazione'));
      }
    } finally {
      setBusy(false);
      setContractualizing(false);
    }
  };

  const doRestore = async () => {
    if (!offer || busy) return;
    setBusy(true);
    try {
      const updated = await restoreOffer(offer.id, offer.row_revision, null);
      toast.success('Offerta ripristinata: tornata in bozza (modificabile)');
      setRestoreOpen(false);
      setOffer(updated);
      if (onChanged) onChanged(updated);
      await reload();
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.detail?.code;
      if (status === 409 && code === 'OFFER_ALREADY_CONTRACTUALIZED') {
        toast.error('Impossibile ripristinare: l\u2019offerta ha già prodotto un subappalto/contratto attivo.');
        setRestoreOpen(false);
        await reload();
      } else if (status === 409) {
        handle409();
        setRestoreOpen(false);
      } else if (status === 400 && code === 'OFFER_NOT_RESTORABLE') {
        toast.error('Solo un\u2019offerta annullata può essere ripristinata.');
        setRestoreOpen(false);
        await reload();
      } else {
        toast.error(extractApiError(err, 'Errore ripristino offerta'));
      }
    } finally {
      setBusy(false);
    }
  };

  const openContract = () => {
    if (!offer?.contractualized_subcontract_id) return;
    // Naviga verso la pagina del contratto (route esistente lato app: /subcontracts/:id oppure query param)
    // Utilizzo apertura in nuova tab per non perdere il contesto corrente.
    const url = `/subcontracts/${offer.contractualized_subcontract_id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onActionClick = (a) => {
    if (a._action === 'delete') doDelete();
    else if (a._action === 'new_rev') setNewRevOpen(true);
    else if (a._action === 'accept') setAcceptModalOpen(true);
    else if (a._action === 'contractualize') setContractualizeOpen(true);
    else if (a._action === 'restore') setRestoreOpen(true);
    else if (a._action === 'edit') {
      if (onRequestEdit && offer) {
        onOpenChange(false);
        // Piccolo defer per garantire chiusura del dialog corrente prima di aprire il wizard
        setTimeout(() => onRequestEdit(offer), 50);
      } else {
        toast.error('Handler di modifica non disponibile');
      }
    }
    else if (a.target) setConfirmTransition({ target: a.target });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="offer-detail-dialog">
        <DialogHeader>
          <DialogTitle data-testid="offer-detail-title">
            {offer ? `${offer.offer_number} · Rev. ${offer.offer_revision_number}` : 'Offerta di Subappalto'}
          </DialogTitle>
          {offer && (
            <div className="mt-2 flex items-center gap-2">
              <Badge
                className={`${OFFER_STATUS_COLORS[offer.status]} ${offer.status === 'contrattualizzato' ? 'uppercase tracking-wide font-semibold' : ''}`}
                data-testid="offer-detail-status"
              >
                {offer.status === 'contrattualizzato' ? 'CONTRATTUALIZZATA' : OFFER_STATUS_LABELS[offer.status]}
              </Badge>
              {offer.is_current_revision === false && (
                <span className="text-xs text-amber-700" data-testid="offer-detail-not-current-warning">
                  (Revisione non corrente — sola lettura)
                </span>
              )}
            </div>
          )}
        </DialogHeader>

        {loading && <div className="p-6 text-slate-500 text-sm" data-testid="offer-detail-loading">Caricamento...</div>}
        {error && !loading && (
          <Alert variant="destructive" data-testid="offer-detail-error">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {offer && !loading && (
          <>
            {/* F.5 — Sezione "Contratto generato": punto di collegamento permanente Offerta ↔ Contratto */}
            {offer.status === 'contrattualizzato' && (
              <GeneratedContractSection offer={offer} />
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm border-b pb-3">
              <div>
                <div className="text-xs text-slate-500">Subappaltatore</div>
                <div className="font-medium">{offer.supplier_snapshot?.ragione_sociale || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Validità</div>
                <div>{offer.valid_until || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Modalità economica</div>
                <div>
                  {offer.pricing_mode === 'agreed_total' && 'Prezzo pattuito complessivo'}
                  {offer.pricing_mode === 'discount_percent' && 'Ribasso percentuale'}
                  {!offer.pricing_mode && <span className="text-slate-400">Non definita</span>}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Importo complessivo</div>
                <div className="font-semibold" data-testid="offer-detail-total">
                  {offer.agreed_total_amount ? `€ ${Number(offer.agreed_total_amount).toFixed(2)}` : '—'}
                </div>
              </div>
              {isAdmin && (
                <>
                  <div>
                    <div className="text-xs text-slate-500">Valore base interno</div>
                    <div>€ {Number(offer.base_internal_amount || 0).toFixed(2)}</div>
                  </div>
                  {offer.pricing_mode === 'discount_percent' && offer.discount_percent && (
                    <div>
                      <div className="text-xs text-slate-500">Ribasso</div>
                      <div>{Number(offer.discount_percent).toFixed(2)}%</div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="mt-4">
              <div className="text-xs text-slate-500 mb-2">Lavorazioni ({(offer.items || []).length})</div>
              <table className="w-full text-sm border border-slate-200 rounded overflow-hidden" data-testid="offer-detail-items-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-2 text-left">Cat.</th>
                    <th className="p-2 text-left">Codice</th>
                    <th className="p-2 text-left">Descrizione</th>
                    <th className="p-2 text-left">U.M.</th>
                    <th className="p-2 text-right">Qtà</th>
                  </tr>
                </thead>
                <tbody>
                  {(offer.items || []).map(it => (
                    <tr key={it.item_id || it.customer_contract_item_id} className="border-t border-slate-100">
                      <td className="p-2">{it.category}</td>
                      <td className="p-2 font-mono text-xs">{it.code}</td>
                      <td className="p-2">{it.description}</td>
                      <td className="p-2">{it.unit}</td>
                      <td className="p-2 text-right">{it.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* F.3.2 — Dati commerciali persistiti */}
            {(offer.notes || offer.exclusions || offer.conditions) && (
              <div className="mt-4 border-t pt-3 space-y-3" data-testid="offer-detail-documental">
                {offer.notes && (
                  <div>
                    <div className="text-xs text-slate-500">Note</div>
                    <div className="text-sm whitespace-pre-wrap" data-testid="offer-detail-notes">{offer.notes}</div>
                  </div>
                )}
                {offer.exclusions && (
                  <div>
                    <div className="text-xs text-slate-500">Esclusioni</div>
                    <div className="text-sm whitespace-pre-wrap" data-testid="offer-detail-exclusions">{offer.exclusions}</div>
                  </div>
                )}
                {offer.conditions && (
                  <div>
                    <div className="text-xs text-slate-500">Condizioni</div>
                    <div className="text-sm whitespace-pre-wrap" data-testid="offer-detail-conditions">{offer.conditions}</div>
                  </div>
                )}
              </div>
            )}

            {/* Cronologia revisioni */}
            {history && history.revisions && history.revisions.length > 1 && (
              <div className="mt-4 border-t pt-3">
                <div className="text-xs text-slate-500 mb-2">Cronologia revisioni</div>
                <div className="space-y-1" data-testid="offer-detail-history">
                  {history.revisions.map(r => (
                    <div key={r.id} className={`text-sm px-2 py-1 rounded ${r.is_current_revision ? 'bg-orange-50' : 'bg-slate-50'}`}>
                      Rev.{r.offer_revision_number} · {OFFER_STATUS_LABELS[r.status]}
                      {r.revision_reason && <span className="text-xs text-slate-500 ml-2">— {r.revision_reason}</span>}
                      {r.is_current_revision && <span className="ml-2 text-xs font-medium text-orange-700">CORRENTE</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Audit essenziale */}
            {history && Array.isArray(history.audit) && history.audit.length > 0 && (
              <div className="mt-4 border-t pt-3">
                <div className="text-xs text-slate-500 mb-2">Audit essenziale (ultimi 5)</div>
                <div className="space-y-1 text-xs" data-testid="offer-detail-audit">
                  {history.audit.slice(-5).map(a => (
                    <div key={a.audit_id} className="flex items-center gap-2 text-slate-600">
                      <span className="font-mono text-slate-500">{a.created_at?.slice(0, 19).replace('T', ' ')}</span>
                      <span>·</span>
                      <span>{a.event}</span>
                      {a.from_status && a.to_status && <span>: {a.from_status} → {a.to_status}</span>}
                      {a.changed_by_email && <span className="text-slate-500 ml-2">({a.changed_by_email})</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <div className="flex flex-wrap gap-2 w-full items-center justify-end">
            {/* Stato terminale CONTRATTUALIZZATO: nessun'altra azione. Solo apertura contratto (F.5). */}
            {offer?.status === 'contrattualizzato' ? (
              <>
                <Button
                  onClick={openContract}
                  disabled={!offer.contractualized_subcontract_id}
                  className="bg-[#FF7819] hover:bg-[#e56811] text-white"
                  data-testid="offer-detail-open-contract"
                >
                  <ExternalLink className="w-4 h-4 mr-1" /> Apri Contratto
                </Button>
                <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="offer-detail-close-btn">
                  Chiudi
                </Button>
              </>
            ) : (
              <>
                {/* PDF Offerta: disponibile per tutti gli stati diversi da bozza (F.4) */}
                {offer && offer.status !== 'bozza' && (
                  <Button
                    variant="outline"
                    onClick={doGeneratePdf}
                    disabled={busy}
                    data-testid="offer-detail-download-pdf"
                  >
                    <FileDown className="w-4 h-4 mr-1" /> Scarica PDF
                  </Button>
                )}

                {actions.map(a => {
                  const Icon = a.icon;
                  // Alias data-testid per la spec F.3.1: il pulsante 'edit' deve
                  // esporre anche `subcontract-offer-edit-button`.
                  const extraTestId = a._action === 'edit' ? 'subcontract-offer-edit-button' : null;
                  return (
                    <Button
                      key={a.key}
                      onClick={() => onActionClick(a)}
                      disabled={busy}
                      variant={a.variant === 'destructive' ? 'destructive' : (a.variant === 'default' ? 'default' : 'outline')}
                      className={a.variant === 'default' ? 'bg-[#FF7819] hover:bg-[#e56811] text-white' : ''}
                      data-testid={extraTestId || `offer-detail-action-${a.key}`}
                      data-testid-secondary={extraTestId ? `offer-detail-action-${a.key}` : undefined}
                    >
                      {Icon && <Icon className="w-4 h-4 mr-1" />} {a.label}
                    </Button>
                  );
                })}

                <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="offer-detail-close-btn">Chiudi</Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Confirm transition */}
      <AlertDialog open={!!confirmTransition} onOpenChange={(v) => !v && setConfirmTransition(null)}>
        <AlertDialogContent data-testid="offer-detail-transition-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermare transizione a "{OFFER_STATUS_LABELS[confirmTransition?.target] || confirmTransition?.target}"?</AlertDialogTitle>
            <AlertDialogDescription>
              L'operazione aggiorna lo stato dell'offerta. Le revisioni precedenti restano immutabili.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={() => doTransition(confirmTransition.target)}
              data-testid="offer-detail-transition-confirm-btn"
            >
              Conferma
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm restore (ANNULLATO → BOZZA) */}
      <AlertDialog open={restoreOpen} onOpenChange={(v) => !v && setRestoreOpen(false)}>
        <AlertDialogContent data-testid="offer-detail-restore-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Vuoi ripristinare questa offerta di subappalto?</AlertDialogTitle>
            <AlertDialogDescription>
              Tornerà nello stato modificabile (bozza) e potrà essere portata nuovamente
              alla firma del subappaltatore. Numero, revisione e dati economici restano invariati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={doRestore}
              data-testid="offer-detail-restore-confirm-btn"
            >
              Ripristina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal nuova revisione */}
      <Dialog open={newRevOpen} onOpenChange={setNewRevOpen}>
        <DialogContent data-testid="offer-detail-new-revision-modal">
          <DialogHeader>
            <DialogTitle>Nuova revisione</DialogTitle>
            <DialogDescription>
              La revisione corrente verrà marcata "superato" e verrà creata una nuova bozza modificabile.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new_rev_reason">Motivazione (obbligatoria)</Label>
            <Input
              id="new_rev_reason"
              value={newRevReason}
              onChange={(e) => setNewRevReason(e.target.value)}
              placeholder="Es. revisione economica post-negoziazione"
              data-testid="offer-detail-new-revision-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewRevOpen(false)} disabled={busy}>Annulla</Button>
            <Button
              onClick={doNewRevision}
              disabled={busy || !newRevReason.trim()}
              className="bg-[#FF7819] hover:bg-[#e56811] text-white"
              data-testid="offer-detail-new-revision-submit"
            >
              Crea nuova revisione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* F.5 — Modal Contrattualizzazione */}
      <AlertDialog open={contractualizeOpen} onOpenChange={(v) => !v && !contractualizing && setContractualizeOpen(false)}>
        <AlertDialogContent data-testid="offer-detail-contractualize-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Contrattualizzare l&apos;Offerta?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;operazione genera un <strong>Contratto di Subappalto</strong> definitivo a partire dall&apos;Offerta accettata.
              Note, esclusioni, condizioni economiche e lavorazioni verranno congelate nel contratto.
              L&apos;Offerta passer&agrave; in stato <strong>CONTRATTUALIZZATA</strong> e diventer&agrave; documento storico non modificabile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="contractualize_reason">Motivazione (opzionale)</Label>
            <Input
              id="contractualize_reason"
              value={contractualizeReason}
              onChange={(e) => setContractualizeReason(e.target.value)}
              placeholder="Es. approvazione finale post-negoziazione"
              disabled={contractualizing}
              data-testid="offer-detail-contractualize-reason"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={contractualizing} data-testid="offer-detail-contractualize-cancel">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={contractualizing || busy}
              onClick={(e) => { e.preventDefault(); doContractualize(); }}
              className="bg-[#FF7819] hover:bg-[#e56811] text-white"
              data-testid="offer-detail-contractualize-confirm"
            >
              {contractualizing ? (
                <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Contrattualizzazione...</>
              ) : (
                <><FileSignature className="w-4 h-4 mr-1" /> Contrattualizza</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal accettazione */}
      <Dialog open={acceptModalOpen} onOpenChange={setAcceptModalOpen}>
        <DialogContent data-testid="offer-detail-accept-modal">
          <DialogHeader>
            <DialogTitle>Registrare accettazione</DialogTitle>
            <DialogDescription>
              Registra manualmente l'accettazione dell'offerta. In F.3 non è previsto upload documento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="accept_date">Data accettazione</Label>
              <Input
                id="accept_date"
                type="date"
                value={acceptDate}
                onChange={(e) => setAcceptDate(e.target.value)}
                data-testid="offer-detail-accept-date"
              />
            </div>
            <div>
              <Label htmlFor="accept_notes">Note</Label>
              <Input
                id="accept_notes"
                value={acceptNotes}
                onChange={(e) => setAcceptNotes(e.target.value)}
                placeholder="Note libere (facoltative)"
                data-testid="offer-detail-accept-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAcceptModalOpen(false)} disabled={busy}>Annulla</Button>
            <Button
              onClick={doAccept}
              disabled={busy}
              className="bg-[#FF7819] hover:bg-[#e56811] text-white"
              data-testid="offer-detail-accept-confirm"
            >
              Conferma accettazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default SubcontractOfferDetail;
